import React from "react";
import cn from "./cn";

/**
 * Switch — an on/off control that takes effect immediately.
 *
 *   <Switch checked={enabled} onChange={setEnabled}
 *           label="Enable Public API"
 *           description="External websites can then read your course catalog." />
 *
 *   <Switch checked={row.active} onChange={…} aria-label={`Activate ${row.name}`} />
 *
 * ── WHY IT EXISTS ────────────────────────────────────────────────────────────
 * Thirteen hand-rolled toggles across eleven files, under ten different class
 * prefixes — `papi-toggle__thumb`, `settings-toggle__thumb` (×3), `rzp-toggle__thumb`,
 * `tel-toggle__thumb`, `ca-toggle__thumb`, `aid-toggle-slider`, `bf-toggle-slider`,
 * `cms-mgmt-switch__slider`, `cms-mgmt-toggle-knob`, `sp-toggle-thumb`. Every one is
 * a 44×24 track with a 20px thumb that translates 20px in 0.2s, and they drift only
 * in the parts nobody chose: shadow alpha 0.1 vs 0.15, disabled opacity 0.45 vs 0.5,
 * thumb inset 2px vs 3px.
 *
 * ── IT IS A SWITCH, NOT A CHECKBOX ───────────────────────────────────────────
 * `role="switch"` with `aria-checked`, because "on/off, applied now" is a different
 * promise from "ticked, and it will be saved when you submit". Reach for `Checkbox`
 * when the value is part of a form the user still has to save.
 *
 * ── THE NAME IS NOT OPTIONAL ─────────────────────────────────────────────────
 * A bare `<button role="switch">` announces as "switch, off" and nothing else, which
 * is what every one of the thirteen did — several sat beside a `<span>` that LOOKED
 * like a label and named nothing. Pass `label` (rendered, and wired by id) or
 * `aria-label` for a switch whose meaning comes from its column or row.
 *
 * Props (mirroring `Checkbox`, so the two read the same at a call site):
 *   checked     — controlled state
 *   onChange    — called with the NEXT boolean, not an event
 *   label       — visible label; renders the whole row, control on the right
 *   description — a second line under the label
 *   disabled    — non-interactive and dimmed
 *   reverse     — put the control on the LEFT of the label
 */

// 44×24 track, 20px thumb, 2px inset, 20px of travel. `tw-shrink-0` because this
// lives at the end of a flex row whose label is allowed to be long.
const TRACK_CLASSES =
  "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-shrink-0 tw-rounded-full tw-border-0 tw-p-0 " +
  "tw-transition-colors tw-duration-200 tw-focus-ring " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

// Track and thumb are `--primary-solid` / `--primary-solid-fg` — the brand FILL
// and the ink that sits on it — not `#fff` and not `--card-bg`. A card token here
// is white in light mode and a DARK surface in dark mode, which is how two other
// controls in this app ended up with an invisible glyph (b27).
// ⚠️ NOT the plain `--primary` pair: that one is lifted in dark mode so it reads
// as ink on a card, which leaves white failing on it as a fill. See the note
// beside --primary-solid in styles/tokens/_palette.scss.
const THUMB_CLASSES =
  "tw-pointer-events-none tw-absolute tw-left-0.5 tw-top-0.5 tw-h-5 tw-w-5 tw-rounded-full " +
  "tw-bg-primary-solid-fg tw-shadow-card tw-transition-transform tw-duration-200";

const Switch = React.forwardRef(
  (
    {
      checked = false,
      onChange,
      label,
      description,
      disabled = false,
      reverse = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const id = React.useId();
    const labelId = label ? `${id}-label` : undefined;
    const descriptionId = description ? `${id}-desc` : undefined;

    const control = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        className={cn(
          TRACK_CLASSES,
          checked ? "tw-bg-primary-solid" : "tw-bg-line",
          !disabled && "tw-cursor-pointer",
          !label && className
        )}
        {...props}
      >
        <span className={cn(THUMB_CLASSES, checked && "tw-translate-x-5")} />
      </button>
    );

    // No label means the meaning comes from somewhere else — a table column, a
    // row already named. Same contract `Checkbox` uses for its bare box.
    if (!label) return control;

    return (
      <div
        className={cn(
          "tw-flex tw-items-start tw-justify-between tw-gap-4",
          reverse && "tw-flex-row-reverse tw-justify-end",
          className
        )}
      >
        <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
          {/* A flex row rather than plain text, because several call sites put a
              lucide glyph in front of the label. A string label is one flex item
              and renders identically. */}
          <span
            id={labelId}
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content [&>svg]:tw-shrink-0 [&>svg]:tw-text-content-muted"
          >
            {label}
          </span>
          {description && (
            <span id={descriptionId} className="tw-text-xs tw-leading-snug tw-text-content-subtle">
              {description}
            </span>
          )}
        </span>
        {control}
      </div>
    );
  }
);

Switch.displayName = "Switch";
export default Switch;
